import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { env } from "@/shared/config/env";
import { NAV_GROUPS } from "@/shared/lib/navItems";
import { hasMinRole } from "@/shared/auth/permissions";
import { selectUser, useAuth } from "@/shared/auth/store";
import { Brand } from "@/shared/ui/Brand";
import { AccountMenu } from "@/widgets/AccountMenu";

/**
 * Desktop primary navigation. The menu itself lives in shared/lib/navItems
 * so the mobile sheet renders the identical list — this file is only the
 * desktop presentation of it.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const role = useAuth(selectUser)?.role;

  // Hide items above the user's role, then drop any group left empty so we
  // don't render an orphan title/divider.
  const visibleGroups = NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.minRole || hasMinRole(role, it.minRole)),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <aside
      aria-label="Primary"
      className={cn(
        "h-full w-[var(--sidebar-w)] shrink-0",
        "bg-[var(--bg)] border-r border-[var(--separator)]",
        "flex flex-col",
      )}
    >
      {/* ── Brand row ─────────────────────────────────────────────── */}
      <div className="h-[var(--header-h)] px-3 flex items-center border-b border-[var(--separator)]">
        <Brand height={20} />
      </div>
      {/* 제품명 — 레일 폭(196px)에 한 줄로는 안 들어간다. 예전에는 로고 옆에 `truncate` 로 붙어
          「유해가스 안전…」처럼 잘려 나왔다. 줄바꿈을 허용해 이름을 온전히 보여 준다. */}
      <div className="px-3 pt-2.5 pb-0.5">
        <p className="text-[11px] leading-[1.35] font-medium tracking-tight text-[var(--fg-muted)]">
          {t("common.productName")}
        </p>
      </div>

      {/* ── Navigation ───────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto py-3">
        {visibleGroups.map((g, gi) => (
          <div key={gi} className={cn(gi > 0 && "mt-3 pt-3 border-t border-[var(--separator)]")}>
            {g.titleKey && (
              <div className="px-3 mb-1 text-[10.5px] uppercase tracking-[0.07em] text-[var(--fg-subtle)] font-semibold">
                {t(g.titleKey)}
              </div>
            )}
            <ul className="px-2 space-y-0.5">
              {g.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.to === "/"}
                    // The 2-pixel active rail is rendered via the
                    // `before:` pseudo-element so we don't need NavLink's
                    // children render-prop. The render-prop pattern can
                    // produce mismatched hook counts when StrictMode
                    // replays the render.
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 px-2.5 h-9 rounded-[var(--radius-md)]",
                        "text-[14px] tracking-[-0.005em]",
                        "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)]",
                        "before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:rounded-r-full",
                        isActive
                          ? "bg-[var(--bg-subtle)] text-[var(--fg)] font-semibold before:bg-[var(--accent)]"
                          : "text-[var(--fg-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] before:bg-transparent",
                      )
                    }
                  >
                    {/* Tinted by group, not by status — see --nav-* in
                        tokens.css. The hue stays constant through the active
                        state: "where am I" is already carried by the rail,
                        the filled row and the bolder label, so recolouring
                        the icon too would only blur the group identity. */}
                    <it.icon
                      size={16}
                      strokeWidth={1.75}
                      className="shrink-0"
                      style={{ color: `var(${g.tone})` }}
                    />
                    <span className="flex-1 truncate">{t(it.labelKey)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* ── Account footer ───────────────────────────────────────── */}
      <div className="p-2 border-t border-[var(--separator)]">
        <AccountMenu />
        {/* 버전·빌드번호 상시 표시 (env.build = VITE_BUILD_ID, 빌드 시 주입). */}
        <div className="px-2.5 pt-1.5 text-[10.5px] tabular-nums text-[var(--fg-subtle)] truncate">
          v{env.version} · {env.build}
        </div>
      </div>
    </aside>
  );
}
